import type { ReactElement } from 'react';
import { Box, Image } from '@zillow/constellation-10';
import { cva } from '@/styled-system/css';
import {
  getSpotIllustrationUrl,
  hasDarkIllustration,
  type SpotIllustrationName,
  type SpotIllustrationTone,
} from './digitalLibrary';

export {
  DIGITAL_LIBRARY_BASE_URL,
  getSpotIllustrationUrl,
  LIGHT_ONLY_ILLUSTRATION_NAMES,
  SPOT_ILLUSTRATION_NAMES,
  type SpotIllustrationName,
  type SpotIllustrationTone,
} from './digitalLibrary';

export const SPOT_ILLUSTRATION_SIZE = 120;

export type SpotIllustrationSize = 120 | 160;

/**
 * SpotIllustration — a Zillow Digital Library spot illustration.
 *
 * The art is served from the Digital Library (`delivery.digitallibrary.zillowgroup.com`), the
 * same source design pulls from in Figma, so every app shows the current version of one file
 * instead of its own SVG copy. Pick the art by `name`; the list lives in `SPOT_ILLUSTRATION_NAMES`
 * and every name has a `light` and a `dark` file, except the names in `LIGHT_ONLY_ILLUSTRATION_NAMES`,
 * which show the light file in both modes until the Digital Library adds a dark one.
 *
 * Light and dark switch on their own: both files are rendered lazily and Panda's `_dark` condition
 * (`data-panda-mode="dark"` on an ancestor) shows one and hides the other, so the browser only
 * downloads the visible file. Constellation design (Phil Cho, 2026-10-20) confirmed there is no
 * other web mechanism: use both SVGs. Pass `tone` only to force one file, e.g. a dark card on a
 * light page.
 *
 * Sizes are Constellation's two spot sizes: 120 (default; empty states, cards, error
 * pages) and 160 (onboarding, larger standalone moments). Always decorative: `alt=""` and `aria-hidden`.
 * For a URL outside React (emails, CSS, `next/image`), call `getSpotIllustrationUrl(name, tone)`.
 *
 * **DO NOT**
 * - Copy a Digital Library SVG into an app's `assets/` or `images/` folder
 * - Hard-code the Digital Library URL in an app constants file
 * - Render it at any size other than 120 or 160
 * - Give it alt text
 *
 * If the art you need is not in `SPOT_ILLUSTRATION_NAMES`, check the Digital Library for it and
 * add the name here rather than importing a local file; a name with only a light file also goes in
 * `LIGHT_ONLY_ILLUSTRATION_NAMES`.
 */
export type SpotIllustrationProps = {
  /** Digital Library asset name, e.g. `clipboard` for `clipboard-light_svg_Original.svg`. */
  name: SpotIllustrationName;
  /** 120 (default) or 160. */
  size?: SpotIllustrationSize;
  /** Forces one file. Omit it to follow the page's light/dark mode. */
  tone?: SpotIllustrationTone;
};

const sizeRecipe = cva({
  variants: {
    size: {
      120: { width: '120px', height: '120px' },
      160: { width: '160px', height: '160px' },
    },
  },
});

export function SpotIllustration({ name, size = SPOT_ILLUSTRATION_SIZE, tone }: SpotIllustrationProps): ReactElement {
  const image = (fileTone: SpotIllustrationTone, loading?: 'lazy') => (
    <Image
      src={getSpotIllustrationUrl(name, fileTone)}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      loading={loading}
      css={sizeRecipe.raw({ size })}
      data-testid="SpotIllustration"
    />
  );

  if (tone || !hasDarkIllustration(name)) return image(tone ?? 'light');

  return (
    <>
      <Box css={{ display: 'block', _dark: { display: 'none' } }}>{image('light', 'lazy')}</Box>
      <Box css={{ display: 'none', _dark: { display: 'block' } }}>{image('dark', 'lazy')}</Box>
    </>
  );
}
